import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ConfirmAction } from '@/components/confirm-action'
import { FieldError } from '@/components/field-error'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  PLATFORM_LABEL,
  PLATFORMS,
  promotePatchInputSchema,
  usePatchMessage,
  usePromotePatch,
  useShorebirdReleasePatches,
  useShorebirdReleases,
  useTranslateDraft,
  type Platform,
  type PromotePatchInput,
  type PromotePatchOutcome,
} from '@/features/app-patch-policy/api'
import { useLocale } from '@/features/app-patch-policy/locale'
import { MessageTranslationsDialog } from '@/features/app-patch-policy/message-translations-dialog'

/** A reminder card: testing a staged patch happens on the mobile side (7 taps on the version
 * label in More flips `ShorebirdUpdateService.isStagingTester`, polling "staging" instead of
 * "stable"), not from this console. */
function StagingTesterCard() {
  const { t } = useLocale()
  return (
    <Card>
      <CardTitle>{t('staging.title')}</CardTitle>
      <CardDescription className="mt-1">
        {t('staging.descBefore')}
        <b>More</b>
        {t('staging.descAfter')}
      </CardDescription>
    </Card>
  )
}

/**
 * "Ship a patch" tab: promotes a Shorebird patch from a non-default channel (e.g. "staging") to
 * "stable" — the moment it actually reaches production. The optional "Also set as mandatory"
 * checkbox writes a mandatory-rule row in the same submit (see useSaveMandatoryRule), so "this
 * patch is both the release gate and the floor" doesn't need a separate trip to Section A.
 */
export function PromotePanel() {
  const { t } = useLocale()
  const promote = usePromotePatch()

  const form = useForm<PromotePatchInput>({
    resolver: standardSchemaResolver(promotePatchInputSchema),
    defaultValues: {
      platform: undefined,
      release_version: '',
      patch_number: 0,
      set_mandatory: false,
      title: '',
      message: '',
    },
  })

  const [lastResult, setLastResult] = useState<PromotePatchOutcome | null>(null)

  const setMandatory = form.watch('set_mandatory')
  const patchNumber = form.watch('patch_number')
  const releaseVersion = form.watch('release_version')
  const titleDraft = form.watch('title')
  const messageDraft = form.watch('message')

  // One platform per promotion: a patch number identifies a patch on exactly one of them (see
  // promotePatchInputSchema), so there is nothing to fan out to.
  const browsePlatform = form.watch('platform') ?? null
  // Fetched for both platforms unconditionally (hooks can't be called in a loop) — needed so a
  // platform with zero releases can be disabled before it's ever checked.
  const iosReleasesQuery = useShorebirdReleases('ios')
  const androidReleasesQuery = useShorebirdReleases('android')
  const releasesQueryByPlatform: Record<Platform, typeof iosReleasesQuery> = {
    ios: iosReleasesQuery,
    android: androidReleasesQuery,
  }
  const releasesQuery = browsePlatform ? releasesQueryByPlatform[browsePlatform] : iosReleasesQuery
  // Derived from the form's own release_version rather than tracked in separate state: staff can
  // type release_version directly instead of using the picker (see the manual patch_number Input
  // below for the same reasoning), and separate state desyncs from that free-text edit — the patch
  // list then silently keeps fetching the previously-picked release while the form shows another.
  const selectedReleaseId = releasesQuery.data?.find((r) => r.version === releaseVersion)?.id ?? null
  function noReleasesFor(platform: Platform) {
    const query = releasesQueryByPlatform[platform]
    return query.isSuccess && (query.data?.length ?? 0) === 0
  }
  // Clear the platform the moment it's confirmed to have zero releases, even if it was picked
  // before that was known.
  useEffect(() => {
    if (!browsePlatform || !noReleasesFor(browsePlatform)) return
    form.resetField('platform')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iosReleasesQuery.isSuccess, iosReleasesQuery.data, androidReleasesQuery.isSuccess, androidReleasesQuery.data])
  const translateDraft = useTranslateDraft(browsePlatform ?? 'ios', releaseVersion, patchNumber)
  const patchesQuery = useShorebirdReleasePatches(selectedReleaseId, browsePlatform)
  // Only "staging" patches are worth promoting — one already on stable has nothing to gain from
  // being promoted again. The manual Input below still covers deliberately re-promoting something.
  const stagingPatches = patchesQuery.data?.filter((patch) => patch.channel === 'staging') ?? []
  const currentStablePatch = patchesQuery.data?.find((patch) => patch.channel === 'stable') ?? null
  // Matched against stagingPatches (what the Select renders), not the full patchesQuery.data — a
  // typed number already on stable must resolve to no selection, not to a blank-looking trigger.
  const selectedPatchId = stagingPatches.find((p) => p.number === patchNumber)?.id ?? null
  const [translationsOpen, setTranslationsOpen] = useState(false)

  // A release/patch pair belongs to the platform it was picked under, so switching platform has to
  // clear the submitted values too, not just the pickers — otherwise the impact summary (and the
  // submit itself) keeps carrying the previous platform's numbers. title/message are left alone
  // here (release_version resetting to '' makes the prefill effect below a no-op until a fresh
  // release_version+patch_number is actually picked).
  useEffect(() => {
    form.setValue('release_version', '', { shouldDirty: false, shouldValidate: false })
    form.setValue('patch_number', 0, { shouldDirty: false, shouldValidate: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePlatform])

  // Whatever this exact release_version+patch_number already has, shown the moment it's picked —
  // same reasoning as mandatory-rules-card.tsx: since the message is no longer one persistent row
  // per platform, this is the only way staff see (or knowingly overwrite) an existing patch's copy
  // instead of it silently staying invisible.
  const patchMessage = usePatchMessage(browsePlatform, releaseVersion, patchNumber)
  // Prefills once per distinct (platform, release, patch), so a late refetch never stomps what
  // staff already started typing.
  const prefilledKeyRef = useRef<string | null>(null)
  // Platform actually last processed here — distinct from browsePlatform so we can tell "just
  // switched platform" apart from "picked a different patch on the same platform" below.
  const lastProcessedPlatformRef = useRef<Platform | null>(null)
  // What this effect itself last wrote into each field — not RHF's isDirty, which recomputes
  // against defaultValues on unrelated setValue calls and can't be trusted to mean "user-typed".
  const lastPrefilledRef = useRef({ title: '', message: '' })
  useEffect(() => {
    if (!browsePlatform || releaseVersion.trim() === '' || patchNumber <= 0 || !patchMessage.isSuccess) return
    // Skip a stale render: the platform-switch reset effect above (must stay declared before this
    // one) already cleared release/patch in form state, but this render's watched values are still the old ones.
    if (form.getValues('release_version') !== releaseVersion || form.getValues('patch_number') !== patchNumber) return
    const key = `${browsePlatform}:${releaseVersion}:${patchNumber}`
    if (prefilledKeyRef.current === key) return
    const isPlatformSwitch =
      lastProcessedPlatformRef.current !== null && lastProcessedPlatformRef.current !== browsePlatform
    prefilledKeyRef.current = key
    lastProcessedPlatformRef.current = browsePlatform
    // Server copy for this patch wins; else a platform switch keeps a field only if it differs
    // from our last prefill (staff edited it), while same-platform patch changes clear it.
    if (patchMessage.data?.title || !(isPlatformSwitch && form.getValues('title') !== lastPrefilledRef.current.title)) {
      const nextTitle = patchMessage.data?.title ?? ''
      form.setValue('title', nextTitle, { shouldDirty: false, shouldValidate: false })
      lastPrefilledRef.current.title = nextTitle
    }
    if (
      patchMessage.data?.message ||
      !(isPlatformSwitch && form.getValues('message') !== lastPrefilledRef.current.message)
    ) {
      const nextMessage = patchMessage.data?.message ?? ''
      form.setValue('message', nextMessage, { shouldDirty: false, shouldValidate: false })
      lastPrefilledRef.current.message = nextMessage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePlatform, releaseVersion, patchNumber, patchMessage.isSuccess, patchMessage.data])

  function clearMessageDraft() {
    form.setValue('title', '', { shouldDirty: true, shouldValidate: true })
    form.setValue('message', '', { shouldDirty: true, shouldValidate: true })
  }

  function pickPlatform(platform: Platform) {
    form.setValue('platform', platform, { shouldDirty: true, shouldValidate: true })
  }

  function pickRelease(releaseId: number) {
    const release = releasesQuery.data?.find((r) => r.id === releaseId)
    if (release) {
      form.setValue('release_version', release.version, { shouldDirty: true, shouldValidate: true })
    }
    form.setValue('patch_number', 0, { shouldDirty: true, shouldValidate: true })
  }

  function pickPatch(patchId: number) {
    const patch = patchesQuery.data?.find((p) => p.id === patchId)
    if (patch) {
      form.setValue('patch_number', patch.number, { shouldDirty: true, shouldValidate: true })
    }
  }

  // Promote affects production, so submit only stages the values; ConfirmAction below runs it.
  const [pendingValues, setPendingValues] = useState<PromotePatchInput | null>(null)

  function onSubmit(values: PromotePatchInput) {
    setLastResult(null)
    setPendingValues(values)
  }

  async function confirmPromote() {
    if (!pendingValues) return
    await promote.mutateAsync(pendingValues, {
      onSuccess: (outcome) => {
        setLastResult(outcome)
        // Without this, coming back to this same (platform, release, patch) later is blocked
        // from prefilling again — its key never changed — and shows stale title/message text.
        prefilledKeyRef.current = null
      },
    })
  }

  // Translations and blur-to-translate need a real patch to scope to, never patch_number=0.
  const patchSelected = browsePlatform !== null && releaseVersion.trim() !== '' && patchNumber > 0

  // Submit waits only while the message query is still loading (not forever on error, or an
  // emergency Promote could get stuck blocked with no way through) — see the warning it shows below.
  const messageQueryPending = patchSelected && patchMessage.isPending

  return (
    <div className="space-y-4">
      <StagingTesterCard />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
        <Card>
          <CardTitle>{t('promote.title')}</CardTitle>
          <CardDescription className="mt-1">{t('promote.description')}</CardDescription>

          <form className="mt-5 space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>{t('promote.platformLabel')}</Label>
                <div className="flex flex-col gap-2 pt-1">
                  {PLATFORMS.map((platform) => {
                    const disabled = noReleasesFor(platform)
                    return (
                      <label
                        key={platform}
                        className={`flex items-center gap-2 text-sm ${disabled ? 'text-muted-foreground' : ''}`}
                      >
                        <input
                          type="radio"
                          name="promote-platform"
                          className="size-4 accent-primary"
                          checked={browsePlatform === platform}
                          disabled={disabled}
                          onChange={() => pickPlatform(platform)}
                        />
                        {PLATFORM_LABEL[platform]}
                        {disabled && <span className="text-xs">{t('promote.noReleasesYet')}</span>}
                      </label>
                    )
                  })}
                </div>
                <FieldError message={form.formState.errors.platform?.message} />
                <p className="text-xs text-muted-foreground">{t('promote.singlePlatformNote')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="promote-release-version">{t('promote.releaseVersionLabel')}</Label>
                {browsePlatform ? (
                  <Select
                    value={selectedReleaseId !== null ? String(selectedReleaseId) : undefined}
                    onValueChange={(value) => pickRelease(Number(value))}
                    disabled={releasesQuery.isPending || releasesQuery.isError}
                  >
                    <SelectTrigger id="promote-release-picker">
                      <SelectValue
                        placeholder={
                          releasesQuery.isPending ? t('promote.pickerLoadingReleases') : t('promote.pickerPromptRelease')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {releasesQuery.data?.map((release) => (
                        <SelectItem key={release.id} value={String(release.id)}>
                          {release.version} ({release.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('promote.checkPlatformFirst')}</p>
                )}
                {releasesQuery.isError && <p className="text-xs text-warning">{t('promote.releasesLoadError')}</p>}
                <Input
                  id="promote-release-version"
                  placeholder={t('promote.releaseVersionPlaceholder')}
                  {...form.register('release_version')}
                />
                <FieldError message={form.formState.errors.release_version?.message} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="promote-patch-number">{t('promote.patchToPromoteLabel')}</Label>
                {selectedReleaseId !== null && !patchesQuery.isPending && !patchesQuery.isError && (
                  <p className="text-xs text-muted-foreground">
                    {currentStablePatch
                      ? `Currently on stable: patch ${currentStablePatch.number}.`
                      : 'Nothing on stable yet for this release.'}
                  </p>
                )}
                {currentStablePatch && patchNumber > 0 && patchNumber < currentStablePatch.number && (
                  <p className="text-xs text-warning">
                    Patch {patchNumber} is older than what's live on stable (patch{' '}
                    {currentStablePatch.number}) — submitting this rolls stable backward, same as the
                    Rollback section below.
                  </p>
                )}
                {selectedReleaseId !== null ? (
                  <Select
                    value={selectedPatchId !== null ? String(selectedPatchId) : undefined}
                    onValueChange={(value) => pickPatch(Number(value))}
                    disabled={patchesQuery.isPending || patchesQuery.isError}
                  >
                    <SelectTrigger id="promote-patch-picker">
                      <SelectValue
                        placeholder={
                          patchesQuery.isPending ? t('promote.pickerLoadingPatches') : t('promote.pickerPromptPatch')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {stagingPatches.map((patch) => (
                        <SelectItem key={patch.id} value={String(patch.id)}>
                          Patch {patch.number}
                          {patch.is_rolled_back ? ' (rolled back)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('promote.pickReleaseFirst')}</p>
                )}
                {patchesQuery.isError && <p className="text-xs text-warning">{t('promote.patchesLoadError')}</p>}
                {!patchesQuery.isPending && !patchesQuery.isError && selectedReleaseId !== null && stagingPatches.length === 0 && (
                  <p className="text-xs text-warning">
                    Nothing on staging for this release — every patch is already on stable or another
                    channel. Type the number below manually if you still mean to promote one.
                  </p>
                )}
                <Input
                  id="promote-patch-number"
                  type="number"
                  min={0}
                  step={1}
                  {...form.register('patch_number', { valueAsNumber: true })}
                />
                <FieldError message={form.formState.errors.patch_number?.message} />
                <p className="text-xs text-faint">{t('promote.requiredHelper')}</p>
              </div>
            </div>

            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={setMandatory}
                  onCheckedChange={(value) =>
                    form.setValue('set_mandatory', Boolean(value), { shouldDirty: true })
                  }
                />
                {t('promote.alsoSetMandatory')}
              </label>
              <p className="text-xs text-muted-foreground">
                {t('promote.mandatoryHelper', { patch: String(patchNumber || '—') })}
              </p>
            </div>

            {patchSelected && patchMessage.isError && (
              <p className="text-xs text-warning">
                Could not load this patch's existing title/message — submitting now will overwrite
                whichever of the two you leave blank instead of keeping it. Reload, or fill in both
                fields to be safe.
              </p>
            )}

            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="promote-title" className="text-xs">
                  {t('message.titleLabel')}
                </Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title={t('promote.clearDraftTooltip')}
                    disabled={!titleDraft && !messageDraft}
                    onClick={clearMessageDraft}
                  >
                    {t('promote.clearDraft')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!patchSelected}
                    onClick={() => setTranslationsOpen(true)}
                  >
                    {t('common.translations')}
                  </Button>
                </div>
              </div>
              <Input
                id="promote-title"
                placeholder={t('message.titlePlaceholder')}
                maxLength={64}
                {...form.register('title')}
              />
              <p className="text-xs text-faint">{t('message.titleHelper')}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="promote-message">{t('message.label')}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!patchSelected}
                  onClick={() => setTranslationsOpen(true)}
                >
                  {t('common.translations')}
                </Button>
              </div>
              <Textarea
                id="promote-message"
                rows={2}
                placeholder={t('promote.messagePlaceholder')}
                {...form.register('message', {
                  onBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => {
                    if (!patchSelected) return
                    const value = event.target.value.trim()
                    if (value) translateDraft.mutate({ title: (form.getValues('title') ?? '').trim(), message: value })
                  },
                })}
              />
              <p className="text-xs text-faint">{t('promote.messageHelper')}</p>
            </div>

            {browsePlatform && translationsOpen && (
              <MessageTranslationsDialog
                platform={browsePlatform}
                releaseVersion={releaseVersion}
                patchNumber={patchNumber}
                englishMessage={patchMessage.data?.message ?? ''}
                open={translationsOpen}
                onOpenChange={setTranslationsOpen}
              />
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={promote.isPending || messageQueryPending}>
                {promote.isPending ? t('promote.submitting') : t('promote.submit')}
              </Button>

              {promote.isError && (
                <span className="text-xs text-destructive">{(promote.error as Error).message}</span>
              )}
            </div>

            <ConfirmAction
              open={pendingValues !== null}
              onOpenChange={(open) => !open && setPendingValues(null)}
              title="Promote this patch to stable?"
              description={
                pendingValues && (
                  <>
                    Patch {pendingValues.patch_number} on {PLATFORM_LABEL[pendingValues.platform]} becomes what real
                    users receive immediately
                    {pendingValues.set_mandatory
                      ? `, and becomes the mandatory floor for ${pendingValues.release_version}.`
                      : '.'}
                  </>
                )
              }
              confirmLabel="Promote to Stable"
              onConfirm={confirmPromote}
            />

            {lastResult && (
              <div className="flex flex-col gap-1">
                <span
                  className={`text-xs ${
                    lastResult.error || lastResult.messageError || lastResult.ruleError
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}
                >
                  {PLATFORM_LABEL[lastResult.platform]}:{' '}
                  {lastResult.error
                    ? lastResult.error
                    : lastResult.result?.already_promoted
                      ? `patch ${lastResult.result.patch_number} was already on stable — nothing to do.`
                      : `patch ${lastResult.result?.patch_number} promoted to stable${
                          lastResult.result?.previous_channel
                            ? ` — was previously on ${lastResult.result.previous_channel}`
                            : ''
                        }.`}
                  {lastResult.ruleSaved &&
                    ` Also set as the mandatory floor for ${lastResult.ruleSaved.release_version} (patch ${lastResult.ruleSaved.patch_number}).`}
                  {lastResult.ruleError && ` Failed to save mandatory floor: ${lastResult.ruleError}`}
                  {lastResult.messageSaved && ' Message/title updated.'}
                  {lastResult.messageError && ` Failed to save message: ${lastResult.messageError}`}
                </span>
              </div>
            )}
          </form>
        </Card>

        <Card className="bg-muted/20">
          <p className="text-[10px] font-bold uppercase tracking-wider text-faint">{t('promote.impactHeading')}</p>
          {!browsePlatform ? (
            <p className="mt-3 text-xs text-muted-foreground">{t('promote.impactNone')}</p>
          ) : (
            <div className="mt-3 space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{t('promote.impactPlatforms')}</span>
                <span className="font-medium">{PLATFORM_LABEL[browsePlatform]}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{t('promote.impactVersion')}</span>
                <span className="font-mono font-medium">
                  {releaseVersion || '—'}
                  {patchNumber ? ` · patch ${patchNumber}` : ''}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{t('promote.impactMandatory')}</span>
                <span className="font-medium">{setMandatory ? t('promote.impactYes') : t('promote.impactNo')}</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
